import type {
    MaintenanceTableItem,
    MaintenanceTableMetric,
    MaintenanceTableMetricSource,
} from '../../api/fetchMaintenanceTable';
import type { Asset } from '../../models/asset';
import type { MaintenanceStatus, WarrantyFilter } from './monitoringUtils';

export interface MonitoringMetric {
    value: number;
    date?: Date;
    source?: MaintenanceTableMetricSource;
}

export interface MonitoringRow {
    id: string;
    chassis: string;
    name?: string;
    type?: string;
    licensePlate?: string;
    accountName?: string;
    odometer?: MonitoringMetric;
    hourmeter?: MonitoringMetric;
    campaignCount: number;
    maintenanceGroup?: string;
    warranty: WarrantyFilter;
    warrantyRaw?: string;
    deviceConnected?: boolean;
    connectivity?: string;
    volksTotal?: string;
    hasContractDetails: boolean;
    lastMaintenance?: { number?: number; checkupType?: string; date?: Date };
    nextMaintenance?: { number?: number; km?: number; status?: MaintenanceStatus };
    loaded: boolean;
    failed?: boolean;
}

const WARRANTY_MAP: Record<string, WarrantyFilter> = {
    UNDER_WARRANTY: 'in',
    OUT_OF_WARRANTY: 'out',
};

const NEXT_STATUS_MAP: Record<string, MaintenanceStatus> = {
    IN_MAINTENANCE: 'manut',
    AVAILABLE_TO_SCHEDULE: 'agendar',
};

const text = (value?: string) => {
    const trimmed = value?.trim();
    return trimmed && trimmed !== '-' ? trimmed : undefined;
};

const toDate = (value?: string) => {
    const date = value ? new Date(value) : undefined;
    return date && !Number.isNaN(date.getTime()) ? date : undefined;
};

const toMetric = (metric?: MaintenanceTableMetric): MonitoringMetric | undefined =>
    metric?.value === undefined || metric.value === null
        ? undefined
        : { value: metric.value, date: toDate(metric.occurredAt), source: metric.source };

const toLocalDate = (value?: string) => {
    if (!value) {
        return undefined;
    }
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

export const toSummaryRow = (asset: Asset): MonitoringRow => ({
    id: asset.id,
    chassis: asset.chassis,
    name: text(asset.name),
    type: asset.type,
    licensePlate: text(asset.licensePlate),
    campaignCount: 0,
    warranty: '',
    hasContractDetails: false,
    loaded: false,
});

export const mergeTableItem = (base: MonitoringRow, item: MaintenanceTableItem): MonitoringRow => {
    const volksTotal = [item.volkstotal, item.truckerPay, item.volksEssentialsPlanContract]
        .map(text)
        .filter(Boolean)
        .join(', ');

    return {
        ...base,
        name: text(item.vehicleName) ?? base.name,
        chassis: text(item.chassis) ?? base.chassis,
        licensePlate: text(item.licensePlate) ?? base.licensePlate,
        accountName: text(item.accountName),
        odometer: toMetric(item.electedMetrics?.odometer),
        hourmeter: toMetric(item.electedMetrics?.hourMeter),
        campaignCount: item.fieldCampaignAmount ?? 0,
        maintenanceGroup: text(item.maintenanceGroup),
        warranty: WARRANTY_MAP[item.warrantyStatus ?? ''] ?? '',
        warrantyRaw: item.warrantyStatus,
        deviceConnected: item.hasDeviceConnection,
        connectivity: text(item.connectivity),
        volksTotal: volksTotal || undefined,
        hasContractDetails: !!text(item.truckerPay) || !!text(item.volksEssentialsPlanContract),
        lastMaintenance: item.lastMaintenance
            ? {
                  number: item.lastMaintenance.number,
                  checkupType: text(item.lastMaintenance.checkupType),
                  date: toLocalDate(item.lastMaintenance.maintenanceDate),
              }
            : undefined,
        nextMaintenance: item.nextMaintenance
            ? {
                  number: item.nextMaintenance.number,
                  km: item.nextMaintenance.kmToNextMaintenance,
                  status: NEXT_STATUS_MAP[item.nextMaintenance.maintenanceStatus ?? ''],
              }
            : undefined,
        loaded: true,
    };
};

export const getSecondaryName = (row: MonitoringRow) =>
    row.name && row.name !== row.chassis && row.name !== row.licensePlate ? row.name : undefined;

export const hasNumber = (value?: number | null): value is number => value !== undefined && value !== null;

export const canEditMetric = (metric?: MonitoringMetric) => metric?.source !== 'asset';
